import {
  BENTO_ANALYTICS_DATA_PROPERTY,
  BENTO_ANALYTICS_SNOWPLOW_APP_ID,
  RESERVATIONS,
  THEME_AGNOSTIC,
  eventActions,
  eventLabels
} from 'reservations/analytics/constants';
import { BentoAnalyticsExtraParams } from 'reservations/analytics/types';
import {
  COMMON_DATETIME_FORMATS,
  formatDateAndTimeForAnalytics
} from 'reservations/base/helpers/datetime';
import {
  SUBMISSION_TYPES,
  type SubmissionType
} from 'reservations/form/providers/BookingFormProvider';

const CANCEL = 'cancel';

const { CHANGE, CLICK, SUBMIT } = eventActions;

const {
  BOOK_NEW_RESERVATION_AFTER_CANCELLATION,
  CANCEL_BOOKING,
  CREATE_BOOKING,
  DATE_SELECTION,
  NEVERMIND_CLICK,
  PARTY_SIZE_SELECTION,
  TIME_SLOT_SELECTION,
  UPDATE_BOOKING
} = eventLabels;

export interface StoreData {
  account: string;
  devMode: boolean;
  indicativeApiKey: string;
  snowplowAppId: typeof BENTO_ANALYTICS_SNOWPLOW_APP_ID;
  theme: typeof THEME_AGNOSTIC;
}

interface EventData {
  submissionType: SubmissionType | typeof CANCEL;
}

interface DateSelectionEventData extends EventData {
  bookingDate: string;
  currentDate: string;
}
interface PartySizeEventData extends EventData {
  partySize: number;
}

interface TimeSlotEventData extends EventData {
  timeSlot: string;
}
interface DateAndTime {
  bookingDate: string;
  currentDate: string;
  timeSlot: string;
}

interface BookingData
  extends DateSelectionEventData,
    PartySizeEventData,
    TimeSlotEventData {}

export const addBentoAnalyticsScriptTag = (
  storeData: StoreData,
  analyticsScriptSrc: string,
  onLoad: (() => void) | null = null
) => {
  const script = document.createElement('script');
  script.src = analyticsScriptSrc;
  script.async = true;
  script.setAttribute(
    BENTO_ANALYTICS_DATA_PROPERTY,
    `${JSON.stringify(storeData)}`
  );

  if (onLoad) {
    script.onload = onLoad;
  }
  document.body.appendChild(script);

  return script;
};

export const _onBentoAnalyticsLoad = async (
  onComplete: (() => void) | null = null
) => {
  // Library currently listens for page load to start the service.
  // Since this script is included after the page is already loaded,
  // we need to start the service manually.
  window?.BentoAnalytics?.service?.start();
  if (onComplete) {
    onComplete();
  }
};

export const getAnalyticsData = (data: Record<string, any>) => {
  const { availability, date, dateAndTime, partySize } = data;
  //The date and time are saved differently when it's a new/updated booking
  //or a cancellation. This conditional logic accounts for both cases
  if (dateAndTime) {
    const dateTimeData = formatDateAndTimeForAnalytics(dateAndTime);

    return {
      partySize,
      ...dateTimeData
    };
  } else {
    const bookingDate = COMMON_DATETIME_FORMATS.YMMdd(date);
    const currentDate = COMMON_DATETIME_FORMATS.YMMdd(new Date());

    return {
      bookingDate,
      currentDate,
      partySize,
      timeSlot: availability
    };
  }
};

export const trackEventWithBentoAnalytics = (
  action: string,
  label: string,
  data:
    | BookingData
    | DateSelectionEventData
    | PartySizeEventData
    | TimeSlotEventData
) => {
  if (window.BentoAnalytics) {
    window.BentoAnalytics.trackEvent(RESERVATIONS, action, label, data);
  }
};

export const trackNewBookingAfterCancellation = (data: Record<string, any>) => {
  const analyticsData = getAnalyticsData(data);
  trackEventWithBentoAnalytics(CLICK, BOOK_NEW_RESERVATION_AFTER_CANCELLATION, {
    submissionType: CANCEL,
    ...analyticsData
  });
};

export const trackCancelBooking = (data: Record<string, any>) => {
  const analyticsData = getAnalyticsData(data);
  trackEventWithBentoAnalytics(SUBMIT, CANCEL_BOOKING, {
    submissionType: CANCEL,
    ...analyticsData
  });
};

export const trackCreateBooking = (data: Record<string, any>) => {
  const analyticsData = getAnalyticsData(data);
  trackEventWithBentoAnalytics(SUBMIT, CREATE_BOOKING, {
    submissionType: SUBMISSION_TYPES.CREATE_FORM,
    ...analyticsData
  });
};

export const trackDateSelection = (
  submissionType: SubmissionType,
  bookingDate: string,
  currentDate: string
) => {
  trackEventWithBentoAnalytics(CHANGE, DATE_SELECTION, {
    submissionType,
    bookingDate,
    currentDate
  });
};

export const trackNevermindClick = (
  dateAndTime: DateAndTime,
  partySize: number
) => {
  trackEventWithBentoAnalytics(CLICK, NEVERMIND_CLICK, {
    submissionType: CANCEL,
    partySize,
    ...dateAndTime
  });
};

export const trackPartySizeSelection = (
  submissionType: SubmissionType,
  partySize: number
) => {
  trackEventWithBentoAnalytics(CHANGE, PARTY_SIZE_SELECTION, {
    submissionType,
    partySize
  });
};

export const trackTimeSlotSelection = (
  submissionType: SubmissionType,
  timeSlot: string
) => {
  trackEventWithBentoAnalytics(CHANGE, TIME_SLOT_SELECTION, {
    submissionType,
    timeSlot
  });
};

export const trackUpdateBooking = (data: Record<string, any>) => {
  const analyticsData = getAnalyticsData(data);
  trackEventWithBentoAnalytics(SUBMIT, UPDATE_BOOKING, {
    submissionType: SUBMISSION_TYPES.MODIFY_FORM,
    ...analyticsData
  });
};

export const trackPageview = (trackPageviewParams: BentoAnalyticsExtraParams) =>
  // pageview will automatically be triggered by BentoAnalytics on
  // full page load, so url and title are set to empty strings
  window.BentoAnalytics.trackPageview('', '', trackPageviewParams);

export const _trackPageviewOnClientSideNavigation = (
  trackPageviewParams: BentoAnalyticsExtraParams
) => {
  if (window.BentoAnalytics) {
    trackPageview(trackPageviewParams);
  }
};
